// lane B: REQUEST-IDENTITY and REQUEST-RECOVERY routes (docs/design/2026-11-02-beta-shared-
// contracts.md §1.3, §2.5). A request's state (delivered for review is not accepted), a person's
// decision on a delivered request (Accept / Revise / Reject), and the one resolve route of an
// interrupted request's recovery (Continue / Take over / Dismiss), which the Inbox item uses.
import { z } from "zod";
import {
  recoveryAccountableVia,
  recoveryAction,
  recoveryCause,
  recoveryState,
  requestDecidedVia,
  requestOrigin,
  requestState,
  requestSubjectKind,
} from "../../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

export const WorkRequest = z
  .object({
    id: Uuid,
    origin: z.enum(requestOrigin),
    requester_membership_id: Uuid.nullable(),
    station_membership_id: Uuid,
    subject_kind: z.enum(requestSubjectKind),
    subject_id: Uuid,
    thread_id: Uuid.nullable(),
    task_id: Uuid.nullable(),
    root_message_id: Uuid.nullable(),
    state: z.enum(requestState),
    /** Delivered and not rejected counts as done unless the company requires an explicit Accept. */
    done: z.boolean(),
    revision: z.int().min(0),
    max_revisions: z.int().min(0),
    continues: z.int().min(0),
    max_continues: z.int().min(0),
    repeat_of_request_id: Uuid.nullable(),
    delivered_at: Timestamp.nullable(),
    delivered_run_id: Uuid.nullable(),
    accepted_at: Timestamp.nullable(),
    accepted_by_membership_id: Uuid.nullable(),
    decided_via: z.enum(requestDecidedVia).nullable(),
    closed_at: Timestamp.nullable(),
    /** The open recovery of the request, when it is interrupted. */
    recovery_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "WorkRequest" });
export type WorkRequest = z.infer<typeof WorkRequest>;

export const WorkRequestList = z
  .strictObject({ data: z.array(WorkRequest) })
  .meta({ id: "WorkRequestList" });

export const WorkRequestListQuery = z
  .strictObject({
    task_id: Uuid.optional(),
    message_id: Uuid.optional(),
    thread_id: Uuid.optional(),
  })
  .meta({ id: "WorkRequestListQuery" });

export const WorkRequestDecide = z
  .strictObject({
    /** `revise` sends a delivered request back (revision + 1, bounded by `max_revisions`). */
    decision: z.enum(["accept", "reject", "revise"]),
    note: z.string().trim().min(1).max(2000).optional(),
  })
  .meta({ id: "WorkRequestDecide" });

export const RequestRecovery = z
  .object({
    id: Uuid,
    request_id: Uuid,
    run_id: Uuid.nullable(),
    attempt: z.int().min(1),
    station_membership_id: Uuid,
    chain_id: Uuid.nullable(),
    cause: z.enum(recoveryCause),
    cause_detail: z.record(z.string(), z.unknown()),
    accountable_membership_id: Uuid.nullable(),
    accountable_seat_id: Uuid.nullable(),
    accountable_via: z.enum(recoveryAccountableVia).nullable(),
    route_history: z.array(z.record(z.string(), z.unknown())),
    state: z.enum(recoveryState),
    resolved_at: Timestamp.nullable(),
    resolved_by_membership_id: Uuid.nullable(),
    resolved_note: z.string().nullable(),
    next_run_id: Uuid.nullable(),
    /** What the person may do now (Continue is gone once the request spent its continues). */
    actions: z.array(z.enum(recoveryAction)),
    created_at: Timestamp,
  })
  .meta({ id: "RequestRecovery" });
export type RequestRecovery = z.infer<typeof RequestRecovery>;

export const RequestRecoveryResolve = z
  .strictObject({
    action: z.enum(recoveryAction),
    /** Required for Dismiss, and for Continue after a refused delivery or a blocked card. */
    note: z.string().trim().min(1).max(2000).optional(),
    /** Take over: the seat to hand the task to (default: the caller's first seat). */
    seat_id: Uuid.optional(),
  })
  .meta({ id: "RequestRecoveryResolve" });

export const requestRecoveryRoutes = {
  "requests.list": defineRoute({
    ...agents,
    id: "requests.list",
    method: "get",
    path: `${base}/requests`,
    tag: "agents",
    summary: "The requests of a task, a message or a thread, newest first",
    access: "member",
    params: CompanyParams,
    query: WorkRequestListQuery,
    response: { status: 200, schema: WorkRequestList },
    errors: [],
    emits: [],
  }),
  "requests.decide": defineRoute({
    ...agents,
    id: "requests.decide",
    method: "post",
    path: `${base}/requests/{id}/decide`,
    tag: "agents",
    summary: "Accept, revise or reject a delivered request",
    access: "member",
    params: CompanyItemParams,
    body: WorkRequestDecide,
    idempotency: "optional",
    response: { status: 200, schema: WorkRequest },
    errors: ["conflict", "forbidden"],
    emits: ["request.accepted", "request.rejected", "wakeup.requested"],
  }),
  "recoveries.get": defineRoute({
    ...agents,
    id: "recoveries.get",
    method: "get",
    path: `${base}/recoveries/{id}`,
    tag: "agents",
    summary: "An interrupted request's recovery: cause, attempt, who is accountable, actions",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: RequestRecovery },
    errors: [],
    emits: [],
  }),
  "recoveries.resolve": defineRoute({
    ...agents,
    id: "recoveries.resolve",
    method: "post",
    path: `${base}/recoveries/{id}/resolve`,
    tag: "agents",
    summary: "Continue, take over or dismiss an interrupted request",
    access: "member",
    params: CompanyItemParams,
    body: RequestRecoveryResolve,
    idempotency: "optional",
    response: { status: 200, schema: RequestRecovery },
    errors: ["conflict", "forbidden"],
    emits: ["recovery.resolved", "wakeup.requested"],
  }),
} as const;
